/**
 * `ContentUnavailable` — Finnish-first fallback rendered by
 * `PortErrorBoundary` whenever a content-port-backed route throws (TA22
 * port-isolation closure, the content-and-knowledge feature Phase A PR C.3).
 *
 * Wired by `RouteRegistry` for every descriptor carrying
 * `portBoundary: 'content'` — see `shell/routes.ts` for the field's
 * contract and the eleven content-port-backed routes that opt into the
 * boundary. Each such route is rendered as
 * `<PortErrorBoundary portName="content" fallback={<ContentUnavailable />}>`,
 * so when the underlying `useArticle` / `useCategories` query rejects (or its consumer re-throws the error
 * to the boundary, as the bespoke routes do), the user reads the
 * Finnish-first prose below instead of React's default error UI.
 *
 * Posture mirrors `routes/AdminForbidden.tsx`: a single `Card` wrapper
 * inside the shell's `<main>` landmark, headline + body prose + a single
 * `react-router` `Link` back to `/` so the user can escape the broken
 * surface without a full-page reload. No diagnostic-region styling —
 * `PortErrorBoundary` owns the developer-facing diagnostic region
 * (`data-testid="port-error-boundary-diagnostic"`); this fallback is
 * the user-facing prose only.
 *
 * Every visible string flows through `useTranslate` against the
 * `content.unavailable.*` key scope (FR-040). Home (`/`) deliberately
 * does NOT mount this boundary — `HomeCards` carries its own inline
 * error UX (see `routes/Home/HomeCards.tsx`'s `home.cards.errorState`
 * branch) and the route-level fallback would replace that
 * graceful-degradation surface with a full takeover, which is the
 * wrong UX per FR-054 (Home stays reachable when content fails).
 */

import type { JSX } from 'react';
import { Link } from 'react-router';

import { useTranslate } from '../../i18n/useTranslate.js';
import { Card } from '../../primitives/Card.js';

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-xl font-semibold';
const bodyClasses = 'm-0 text-base text-foreground';
const linkClasses = 'self-start text-sm font-medium text-primary underline';

export function ContentUnavailable(): JSX.Element {
  const t = useTranslate();

  return (
    <Card className={pageClasses}>
      <h2 className={headingClasses}>{t('content.unavailable.heading')}</h2>
      <p className={bodyClasses}>{t('content.unavailable.body')}</p>
      <Link className={linkClasses} to="/">
        {t('content.unavailable.backHome')}
      </Link>
    </Card>
  );
}
